import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { useCallback, useMemo, useState } from "react";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL, TEXT_TITLE } from "../../style";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES, NOTE_TYPES } from "../../constants";
import FileInput from "../common/FileInput";
import BatchStudentsChecklist, { getSelectedUserIds, toSelectedMap } from "./BatchStudentsChecklist";

export default function DialogBulkCounselingNote({ visible, batchId, students = [], closeDialog }) {
    const [note, setNote] = useState();
    const [loading, setLoading] = useState();
    const [selectedUserIds, setSelectedUserIds] = useState(() => toSelectedMap(students, true));
    const { requestAPI, showToast } = useAppContext();

    const selectedIds = useMemo(() => getSelectedUserIds(selectedUserIds), [selectedUserIds]);

    const addBulkNote = useCallback(() => {
        requestAPI({
            requestPath: `batches/${batchId}/counseling-notes`,
            requestMethod: "POST",
            requestPostBody: {
                note: note?.note?.trim(),
                type: note?.type || null,
                attachment: note?.attachment || null,
                user_ids: selectedIds,
            },
            setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Bulk Counseling Note !", life: 2000 }),
            onResponseReceieved: (result, responseCode) => {
                if (responseCode === 201 && result?.count > 0) {
                    showToast({
                        severity: "success",
                        summary: "Added",
                        detail: `Counseling Note Added To ${result.count} Students`,
                        life: 1500,
                    });
                    setNote();
                    closeDialog();
                } else {
                    showToast({
                        severity: "error",
                        summary: "Failed",
                        detail: result?.error || "Failed To Add Bulk Counseling Note !",
                        life: 2000,
                    });
                }
            },
        });
    }, [batchId, closeDialog, note, requestAPI, selectedIds, showToast]);

    return (
        <Dialog
            header="Bulk Counseling Note"
            visible={visible}
            className="w-11"
            onHide={closeDialog}
            pt={{
                headertitle: { className: TEXT_TITLE },
                content: { className: "overflow-y-auto" },
            }}
        >
            <p className={`${TEXT_SMALL} text-color-secondary m-0 mb-2`}>Will be added only for checked students.</p>

            <FloatLabel className="mt-5">
                <InputTextarea
                    value={note?.note || ""}
                    id="bulk-counseling-note"
                    rows={4}
                    className="w-full"
                    onChange={(e) => setNote((prev) => ({ ...prev, note: e.target.value }))}
                    disabled={loading}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label htmlFor="bulk-counseling-note">Note</label>
            </FloatLabel>

            <FloatLabel className="mt-4">
                <Dropdown
                    value={note?.type}
                    inputId="bulk-counseling-type"
                    options={NOTE_TYPES}
                    optionLabel="Type"
                    className="w-full"
                    onChange={(e) => setNote((prev) => ({ ...prev, type: e.value }))}
                    disabled={loading}
                    pt={{
                        label: { className: TEXT_SMALL },
                        item: { className: TEXT_SMALL },
                    }}
                />
                <label htmlFor="bulk-counseling-type" className={TEXT_SMALL}>
                    Select Type
                </label>
            </FloatLabel>

            <FileInput
                className="mt-3"
                label="Attachment"
                type="image"
                cdn_url={note?.attachment}
                setCDNUrl={(attachment) => setNote((prev) => ({ ...prev, attachment }))}
                disabled={loading}
            />

            <BatchStudentsChecklist
                students={students}
                selectedUserIds={selectedUserIds}
                disabled={loading}
                onToggle={(userId, checked) => setSelectedUserIds((prev) => ({ ...prev, [userId]: checked }))}
                onCheckAll={(checked) => setSelectedUserIds(toSelectedMap(students, checked))}
            />

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_COUNSELING_NOTE}>
                <Button
                    className="mt-3"
                    label="Add To Selected Students"
                    severity="warning"
                    loading={loading}
                    disabled={!note?.note?.trim() || !selectedIds.length}
                    onClick={addBulkNote}
                    pt={{
                        label: { className: TEXT_NORMAL },
                        icon: { className: TEXT_NORMAL },
                    }}
                />
            </HasRequiredAuthority>
        </Dialog>
    );
}
